import React, { useState, useRef, useEffect, useCallback } from 'react'
import Webcam from 'react-webcam'
import PhotoRealisticAvatar from './PhotoRealisticAvatar'
import Avatar from './Avatar'
import ErrorBoundary from './ErrorBoundary'
import { alsokTheme, componentThemes } from '../styles/alsok-theme'
import { avatarService } from '../services/avatarService'
import { receptionStatusManager } from '../services/receptionStatusManager'
import { chatHistoryManager } from '../services/chatHistoryManager'
import { useReceptionStatus } from '../hooks/useReceptionStatus'

const AdminReceptionLayout = ({ 
  avatarType = 'photo',
  selectedAvatarId = 'sato',
  currentUser,
  receptionStatus: initialReceptionStatus,
  onModeSwitch,
  onNavigateToAdmin
}) => {
  // グローバル受付状態の管理
  const receptionStatusHook = useReceptionStatus()
  const currentReceptionStatus = receptionStatusHook.status
  const webcamRef = useRef(null)
  const avatarRef = useRef(null)
  const chatHistoryRef = useRef(null)
  const notificationPanelRef = useRef(null)
  
  const [speechText, setSpeechText] = useState('')
  const [faceResult, setFaceResult] = useState('')
  const [isListening, setIsListening] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)
  const [isSpeaking, setIsSpeaking] = useState(false)
  const [error, setError] = useState('')
  const [avatarMessage, setAvatarMessage] = useState('')
  const [avatarStatus, setAvatarStatus] = useState('ready')
  const [faceAuthEnabled, setFaceAuthEnabled] = useState(true)
  const [currentVisitor, setCurrentVisitor] = useState(null)
  const [currentAudio, setCurrentAudio] = useState(null)
  const [currentTime, setCurrentTime] = useState(new Date())
  const [chatHistory, setChatHistory] = useState(() => {
    // グローバルチャット履歴から初期化（常に既存の履歴を使用）
    const globalChatHistory = chatHistoryManager.getChatHistory()
    
    if (globalChatHistory.length > 0) {
      console.log('🔄 AdminReceptionLayout: 既存のグローバル履歴を使用:', globalChatHistory.length, '件')
      return globalChatHistory
    }
    
    // グローバル履歴が空の場合は、空の履歴を初期化
    const initialHistory = []
    
    // グローバル履歴に空の履歴を初期化（初回のみ）
    console.log('🆕 AdminReceptionLayout: 新しい履歴を初期化')
    chatHistoryManager.initializeHistory('')
    
    return initialHistory
  })
  const [registeredVisitors, setRegisteredVisitors] = useState([])
  
  // 管理者専用の状態
  const [adminControls, setAdminControls] = useState({
    autoResponse: true,
    debugMode: false,
    visitorCount: 6, // サンプルデータの来客数に合わせて設定
    systemStatus: 'normal'
  })
  const [systemLogs, setSystemLogs] = useState([
    {
      timestamp: new Date(new Date().getTime() - 120000), // 2分前
      message: 'システムが正常に起動しました',
      type: 'success'
    },
    {
      timestamp: new Date(new Date().getTime() - 60000), // 1分前
      message: 'Azure Face API接続を確認しました',
      type: 'info'
    },
    {
      timestamp: new Date(new Date().getTime() - 30000), // 30秒前
      message: 'Web Speech API が利用可能です',
      type: 'info'
    },
    {
      timestamp: new Date(new Date().getTime() - 15000), // 15秒前
      message: 'カメラデバイスが正常に接続されました',
      type: 'success'
    },
    {
      timestamp: new Date(new Date().getTime() - 5000), // 5秒前
      message: '管理者モードが開始されました',
      type: 'info'
    }
  ])
  
  const [visitorStats, setVisitorStats] = useState([
    {
      timestamp: new Date(new Date().setHours(9, 30, 0, 0)), // 9:30
      visitorName: '田中太郎',
      company: 'A社',
      purpose: '企画部との定例会議'
    },
    {
      timestamp: new Date(new Date().setHours(10, 15, 0, 0)), // 10:15
      visitorName: '佐藤花子',
      company: 'B社',
      purpose: '新商品の提案'
    },
    {
      timestamp: new Date(new Date().setHours(11, 0, 0, 0)), // 11:00
      visitorName: '鈴木次郎',
      company: 'C社',
      purpose: 'システム導入の打ち合わせ'
    },
    {
      timestamp: new Date(new Date().setHours(13, 30, 0, 0)), // 13:30
      visitorName: '高橋美咲',
      company: 'D社',
      purpose: '契約書の確認'
    },
    {
      timestamp: new Date(new Date().setHours(14, 45, 0, 0)), // 14:45
      visitorName: '山田一郎',
      company: 'E社',
      purpose: '月次報告会'
    },
    {
      timestamp: new Date(new Date().setHours(15, 20, 0, 0)), // 15:20
      visitorName: '伊藤洋子',
      company: 'F社',
      purpose: 'セキュリティ監査'
    }
  ])
  const [notifications, setNotifications] = useState([])
  

  // グローバル状態変更の監視
  useEffect(() => {
    console.log('📡 AdminReceptionLayout: グローバル状態リスナーを登録中...')
    
    const unsubscribe = receptionStatusManager.addListener((event) => {
      console.log('🔄 AdminReceptionLayout: グローバル状態変更を受信:', event.current)
      const previousStatus = currentReceptionStatus.status
      const newStatus = event.current.status
      
      // 受付状態が変更された場合の処理
      if (previousStatus !== newStatus) {
        console.log('📝 AdminReceptionLayout: 受付状態変更:', previousStatus, '->', newStatus)
        
        // メンテナンスまたは停止状態に入った場合、会話ログをクリア
        if (newStatus === 'maintenance' || newStatus === 'inactive') {
          console.log('🗑️ AdminReceptionLayout: メンテナンス/停止状態のため会話ログをクリア')
          chatHistoryManager.clearHistory()
          addSystemLog(`受付状態が${newStatus === 'maintenance' ? 'メンテナンス' : '停止'}状態に変更されました。会話ログをクリアしました。`, 'info')
        }
        
        // メンテナンスから通常状態に戻った場合、復旧メッセージを追加
        if (previousStatus === 'maintenance' && newStatus === 'active') {
          console.log('✅ AdminReceptionLayout: メンテナンスから復旧')
          const recoveryMessage = {
            type: 'ai',
            message: 'メンテナンスが完了しました。通常通りご利用いただけます。',
            timestamp: new Date()
          }
          // 少し遅延して追加（状態変更後に確実に追加するため）
          setTimeout(() => {
            chatHistoryManager.addMessage(recoveryMessage)
          }, 100)
          addSystemLog('メンテナンスが完了し、通常運用に復帰しました', 'success')
        }
      }
    })
    
    return () => {
      console.log('🗑️ AdminReceptionLayout: グローバル状態リスナーを削除')
      unsubscribe()
    }
  }, [currentReceptionStatus.status])

  // チャット履歴の同期
  useEffect(() => {
    console.log('📡 AdminReceptionLayout: チャット履歴リスナーを登録中...')
    
    // マウント時に最新の履歴を取得
    const latestHistory = chatHistoryManager.getChatHistory()
    if (latestHistory.length > 0) {
      console.log('🔄 AdminReceptionLayout: マウント時に最新履歴を取得:', latestHistory.length, '件')
      setChatHistory([...latestHistory])
    }
    
    const unsubscribe = chatHistoryManager.addListener((event) => {
      console.log('🔄 AdminReceptionLayout: チャット履歴変更を受信:', event.type, '履歴件数:', event.history.length)
      
      // 全てのイベントでローカル状態を更新（確実な同期のため）
      if (event.type === 'messageAdded' || event.type === 'messagesAdded' || event.type === 'historyCleared' || event.type === 'historyInitialized' || event.type === 'historyUpdatedFromStorage') {
        console.log('📝 AdminReceptionLayout: ローカル状態を更新中...', event.type)
        setChatHistory([...event.history])
      }
    })
    
    return () => {
      console.log('🗑️ AdminReceptionLayout: チャット履歴リスナーを削除')
      unsubscribe()
    }
  }, [])

  // チャット履歴更新のヘルパー関数
  const addChatMessage = (message) => {
    const newMessage = {
      type: message.type || 'ai',
      message: message.message || '',
      timestamp: message.timestamp || new Date()
    }
    
    // グローバル状態を先に更新（リスナーを通じて他のコンポーネントとローカル状態を同期）
    chatHistoryManager.addMessage(newMessage)
  }

  const addChatMessages = (messages) => {
    const newMessages = messages.map(msg => ({
      type: msg.type || 'ai',
      message: msg.message || '',
      timestamp: msg.timestamp || new Date()
    }))
    
    // グローバル状態を先に更新（リスナーを通じて他のコンポーネントとローカル状態を同期）
    chatHistoryManager.addMessages(newMessages)
  }

  // 通知ドロップダウンの外側クリック処理
  useEffect(() => {
    const handleClickOutside = (event) => {
      const dropdown = document.getElementById('notification-dropdown')
      const notificationButton = event.target.closest('[data-notification-trigger]')
      
      if (dropdown && !notificationButton && !dropdown.contains(event.target)) {
        dropdown.style.display = 'none'
      }
    }

    document.addEventListener('click', handleClickOutside)
    return () => document.removeEventListener('click', handleClickOutside)
  }, [])

  // チャット履歴更新時の自動スクロール
  useEffect(() => {
    if (chatHistoryRef.current) {
      // より確実にスクロールするために複数のタイミングでスクロール
      const scrollToBottom = () => {
        if (chatHistoryRef.current) {
          chatHistoryRef.current.scrollTop = chatHistoryRef.current.scrollHeight
        }
      }
      
      // 即座にスクロール
      scrollToBottom()
      
      // 少し遅延してもう一度スクロール（レンダリング完了後）
      setTimeout(scrollToBottom, 50)
      
      // スムーズスクロール
      setTimeout(() => {
        if (chatHistoryRef.current) {
          chatHistoryRef.current.scrollTo({
            top: chatHistoryRef.current.scrollHeight,
            behavior: 'smooth'
          })
        }
      }, 100)
    }
  }, [chatHistory])

  // 時刻更新
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date())
    }, 1000)
    return () => clearInterval(timer)
  }, [])

  // 通知システム初期化とイベントリスナー
  useEffect(() => {
    // 既存の通知を読み込み
    const existingNotifications = JSON.parse(localStorage.getItem('adminNotifications') || '[]')
    setNotifications(existingNotifications)

    // adminNotificationイベントリスナー
    const handleAdminNotification = (event) => {
      const newNotification = event.detail
      console.log('Admin notification received:', newNotification) // デバッグログ
      
      setNotifications(prev => {
        const updatedNotifications = [newNotification, ...prev]
        // localStorageにも保存
        localStorage.setItem('adminNotifications', JSON.stringify(updatedNotifications))
        return updatedNotifications
      })
      
      // チャット履歴にも追加
      addChatMessage({
        type: 'system',
        message: `🔔 新しい管理者への連絡要請: ${newNotification.message}`
      })
      
      addSystemLog(`管理者連絡要請: ${newNotification.message}`, 'warning')
    }

    // localStorage変更イベントリスナー（別タブでの通知変更を監視）
    const handleStorageChange = (event) => {
      if (event.key === 'adminNotifications' && event.newValue) {
        console.log('📡 AdminReceptionLayout: 別タブでの通知変更を検知')
        try {
          const newNotifications = JSON.parse(event.newValue)
          setNotifications(newNotifications)
        } catch (error) {
          console.error('通知の同期エラー:', error)
        }
      }
    }

    window.addEventListener('adminNotification', handleAdminNotification)
    window.addEventListener('storage', handleStorageChange)
    
    return () => {
      window.removeEventListener('adminNotification', handleAdminNotification)
      window.removeEventListener('storage', handleStorageChange)
    }
  }, [])

  // 通知パネルのスクロール位置を制御
  useEffect(() => {
    if (notificationPanelRef.current) {
      // 通知が更新されたときは最上部にスクロール
      notificationPanelRef.current.scrollTop = 0
    }
  }, [notifications])

  // システムログ追加
  const addSystemLog = (message, type = 'info') => {
    const newLog = {
      timestamp: new Date(),
      message,
      type
    }
    setSystemLogs(prev => [newLog, ...prev.slice(0, 19)]) // 最新20件まで保持
  }

  // 来客統計更新
  const updateVisitorStats = (visitor) => {
    setVisitorStats(prev => [...prev, {
      ...visitor,
      timestamp: new Date()
    }])
    setAdminControls(prev => ({
      ...prev,
      visitorCount: prev.visitorCount + 1
    }))
  }

  // 来客登録処理（管理者モード拡張）
  const handleVisitorRegistration = (registrationData) => {
    setRegisteredVisitors(prev => [...prev, registrationData])
    updateVisitorStats(registrationData)
    addSystemLog(`来客登録: ${registrationData.visitorName}様 (${registrationData.company})`, 'success')
    
    addChatMessages([
      {
        type: 'system',
        message: `${registrationData.visitorName}様（${registrationData.company}）の来客登録が完了しました。`
      },
      {
        type: 'ai',
        message: `${registrationData.visitorName}様、ご登録ありがとうございます。担当の${registrationData.contactPerson}様にご連絡いたします。`
      }
    ])
    
    setAvatarMessage(`${registrationData.visitorName}様、来客登録が完了いたしました。`)
  }

  // 通知処理関数
  const handleNotificationRead = (notificationId) => {
    const updatedNotifications = notifications.map(n => 
      n.id === notificationId ? { ...n, read: true } : n
    )
    setNotifications(updatedNotifications)
    localStorage.setItem('adminNotifications', JSON.stringify(updatedNotifications))
    addSystemLog('通知を既読にしました', 'info')
  }

  const handleNotificationDismiss = (notificationId) => {
    const updatedNotifications = notifications.filter(n => n.id !== notificationId)
    setNotifications(updatedNotifications)
    localStorage.setItem('adminNotifications', JSON.stringify(updatedNotifications))
    addSystemLog('通知を削除しました', 'info')
  }

  const clearAllNotifications = () => {
    setNotifications([])
    localStorage.setItem('adminNotifications', JSON.stringify([]))
    addSystemLog('すべての通知をクリアしました', 'info')
  }

  // 管理者用のクイックアクション（簡略化）
  const handleQuickAction = (action) => {
    switch (action) {
      case 'reset':
        setAdminControls(prev => ({ ...prev, systemStatus: 'normal', visitorCount: 0 }))
        setSystemLogs([])
        setVisitorStats([])
        addSystemLog('システムがリセットされました', 'info')
        break
      case 'maintenance':
        setAdminControls(prev => ({ ...prev, systemStatus: 'maintenance' }))
        addSystemLog('メンテナンスモードが有効化されました', 'warning')
        break
    }
  }

  const captureImage = () => {
    if (webcamRef.current) {
      return webcamRef.current.getScreenshot()
    }
    return null
  }


  return (
    <ErrorBoundary>
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 50%, #cbd5e1 100%)',
        display: 'flex',
        flexDirection: 'column',
        fontFamily: "'Segoe UI', 'Roboto', 'Arial', sans-serif",
        position: 'relative'
      }}>
        {/* スクロールバーのスタイル */}
        <style jsx>{`
          .chat-history-container::-webkit-scrollbar,
          .admin-control-panel::-webkit-scrollbar {
            width: 8px;
          }
          .chat-history-container::-webkit-scrollbar-track,
          .admin-control-panel::-webkit-scrollbar-track {
            background: #f1f5f9;
            border-radius: 4px;
          }
          .chat-history-container::-webkit-scrollbar-thumb,
          .admin-control-panel::-webkit-scrollbar-thumb {
            background: #cbd5e1;
            border-radius: 4px;
          }
          .chat-history-container::-webkit-scrollbar-thumb:hover,
          .admin-control-panel::-webkit-scrollbar-thumb:hover {
            background: #94a3b8;
          }
        `}</style>
        {/* 管理者モード表示バー */}
        <div style={{
          background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)',
          color: 'white',
          padding: '16px 24px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          fontSize: '16px',
          fontWeight: '600',
          boxShadow: '0 4px 12px rgba(30, 64, 175, 0.3)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.2)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
            <span>🔧 管理者受付モード</span>
            <span style={{ 
              background: currentReceptionStatus.status === 'active' ? '#4CAF50' : 
                         currentReceptionStatus.status === 'maintenance' ? '#ff9800' : '#6c757d',
              padding: '4px 8px',
              borderRadius: '4px',
              fontSize: '12px',
              color: 'white',
              fontWeight: 'bold'
            }}>
              {currentReceptionStatus.status === 'active' ? '🟢 稼働中' :
               currentReceptionStatus.status === 'maintenance' ? '🔧 メンテナンス中' : '⚪ 停止中'}
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span>{currentTime.toLocaleTimeString('ja-JP')}</span>
            
            {/* 通知カウンター（未読のみ） */}
            {notifications.filter(n => !n.read).length > 0 && (
              <div 
                data-notification-trigger
                style={{
                  background: '#f44336',
                  color: 'white',
                  borderRadius: '12px',
                  padding: '4px 8px',
                  fontSize: '12px',
                  fontWeight: 'bold',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  cursor: 'pointer',
                  position: 'relative'
                }}
                onClick={() => {
                  const notificationDropdown = document.getElementById('notification-dropdown')
                  if (notificationDropdown) {
                    notificationDropdown.style.display = notificationDropdown.style.display === 'none' ? 'block' : 'none'
                  }
                }}>
                🔔 {notifications.filter(n => !n.read).length}
                
                {/* 通知ドロップダウン */}
                <div 
                  id="notification-dropdown"
                  style={{
                    position: 'absolute',
                    top: '100%',
                    right: '0',
                    background: 'white',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    width: '320px',
                    maxHeight: '400px',
                    overflowY: 'auto',
                    zIndex: 1000,
                    display: 'none',
                    marginTop: '8px'
                  }}
                >
                  <div style={{
                    padding: '12px 16px',
                    borderBottom: '1px solid #eee',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <h4 style={{ 
                      margin: 0, 
                      fontSize: '14px', 
                      fontWeight: 'bold',
                      color: '#333'
                    }}>
                      📮 管理者への連絡
                    </h4>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          const updatedNotifications = notifications.map(n => ({ ...n, read: true }))
                          setNotifications(updatedNotifications)
                          localStorage.setItem('adminNotifications', JSON.stringify(updatedNotifications))
                          addSystemLog('全ての通知を既読にしました', 'info')
                        }}
                        style={{
                          background: '#4CAF50',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          padding: '4px 8px',
                          fontSize: '10px',
                          cursor: 'pointer'
                        }}
                      >
                        全て既読
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          clearAllNotifications()
                        }}
                        style={{
                          background: '#f44336',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          padding: '4px 8px',
                          fontSize: '10px',
                          cursor: 'pointer'
                        }}
                      >
                        全て削除
                      </button>
                    </div>
                  </div>
                  
                  <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                    {notifications.filter(n => !n.read).length === 0 ? (
                      <div style={{
                        textAlign: 'center',
                        color: '#999',
                        padding: '20px',
                        fontSize: '12px'
                      }}>
                        📭 新しい連絡はありません
                      </div>
                    ) : (
                      notifications.filter(n => !n.read).map((notification) => (
                        <div
                          key={notification.id}
                          onClick={(e) => {
                            e.stopPropagation()
                            handleNotificationRead(notification.id)
                          }}
                          style={{
                            background: '#fff',
                            borderBottom: '1px solid #eee',
                            padding: '12px 16px',
                            cursor: 'pointer',
                            transition: 'background-color 0.2s',
                            position: 'relative'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.backgroundColor = '#f0f8ff'
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.backgroundColor = '#fff'
                          }}
                        >
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            marginBottom: '4px'
                          }}>
                            <span style={{ 
                              width: '6px', 
                              height: '6px', 
                              borderRadius: '50%', 
                              background: '#f44336',
                              display: 'inline-block' 
                            }}></span>
                            <span style={{
                              fontSize: '12px',
                              fontWeight: 'bold',
                              color: '#333'
                            }}>
                              🔔 お客様からの連絡要請
                            </span>
                          </div>
                          <div style={{
                            fontSize: '12px',
                            color: '#666',
                            marginBottom: '4px'
                          }}>
                            {notification.message}
                          </div>
                          <div style={{
                            fontSize: '10px',
                            color: '#999',
                            display: 'flex',
                            justifyContent: 'space-between'
                          }}>
                            <span>📍 {notification.location}</span>
                            <span>{new Date(notification.timestamp).toLocaleString('ja-JP')}</span>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            )}
            
            {/* 管理画面戻るボタン */}
            {onNavigateToAdmin && (
              <button
                onClick={onNavigateToAdmin}
                style={{
                  background: 'linear-gradient(135deg, #2196F3 0%, #1976D2 100%)',
                  color: '#fff',
                  border: '2px solid #1976D2',
                  padding: '8px 16px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  boxShadow: '0 4px 8px rgba(33, 150, 243, 0.3)',
                  transition: 'all 0.3s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-2px)'
                  e.target.style.boxShadow = '0 6px 12px rgba(33, 150, 243, 0.4)'
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)'
                  e.target.style.boxShadow = '0 4px 8px rgba(33, 150, 243, 0.3)'
                }}
              >
                ⚙️ 管理画面
              </button>
            )}
            
            <button
              onClick={() => onModeSwitch && onModeSwitch('customer')}
              style={{
                background: '#fff',
                color: '#ff6b35',
                border: 'none',
                padding: '4px 12px',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: 'bold'
              }}
            >
              お客様モードに切替
            </button>
          </div>
        </div>

        <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
          {/* メインコンテンツエリア */}
          <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
            {/* 左側: カメラとアバター */}
            <div style={{
              width: '40%',
              display: 'flex',
              flexDirection: 'column',
              padding: '15px',
              gap: '15px',
              minHeight: 0
            }}>
              {/* Webカメラ */}
              <div style={{
                height: 'calc(100vh - 180px)',
                maxHeight: '500px',
                background: 'rgba(255, 255, 255, 0.98)',
                borderRadius: '16px',
                padding: '24px',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
                border: '1px solid rgba(255, 255, 255, 0.9)',
                display: 'flex',
                flexDirection: 'column',
                backdropFilter: 'blur(20px)'
              }}>
                <h3 style={{ 
                  color: '#1e40af', 
                  marginBottom: '20px',
                  fontSize: '20px',
                  fontWeight: '700',
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px'
                }}>
                  📷 顔認証カメラ
                </h3>
                <div style={{
                  flex: 1,
                  border: '3px solid #1e40af',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  background: '#000',
                  transform: 'scaleX(-1)',
                  minHeight: '300px',
                  maxHeight: '400px',
                  boxShadow: '0 4px 16px rgba(30, 64, 175, 0.2)'
                }}>
                  <Webcam
                    ref={webcamRef}
                    audio={false}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                    videoConstraints={{
                      width: 1280,
                      height: 720,
                      facingMode: 'user'
                    }}
                  />
                </div>
              </div>

            </div>

            {/* 右側: チャットとボタン */}
            <div style={{
              width: '60%',
              display: 'flex',
              flexDirection: 'column',
              padding: '20px',
              minHeight: 0
            }}>
              {/* チャット履歴 */}
              <div style={{
                flex: 1,
                background: 'rgba(255, 255, 255, 0.98)',
                borderRadius: '16px',
                padding: '24px',
                marginBottom: '20px',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
                border: '1px solid rgba(255, 255, 255, 0.9)',
                display: 'flex',
                flexDirection: 'column',
                backdropFilter: 'blur(20px)',
                minHeight: 0,
                maxHeight: 'calc(100vh - 180px)'
              }}>
                <h3 style={{ 
                  color: '#1e40af', 
                  marginBottom: '20px',
                  fontSize: '20px',
                  fontWeight: '700',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  flexShrink: 0
                }}>
                  💬 会話履歴
                </h3>
                <div 
                  ref={chatHistoryRef}
                  className="chat-history-container"
                  style={{
                    flex: 1,
                    overflowY: 'auto',
                    border: '1px solid #e2e8f0',
                    borderRadius: '12px',
                    padding: '16px',
                    background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                    boxShadow: 'inset 0 2px 8px rgba(0, 0, 0, 0.05)',
                    minHeight: 0,
                    maxHeight: '100%',
                    scrollbarWidth: 'thin',
                    scrollbarColor: '#cbd5e1 #f1f5f9'
                  }}
                >
                  {chatHistory.map((chat, index) => (
                    <div key={index} style={{
                      marginBottom: '15px',
                      display: 'flex',
                      flexDirection: chat.type === 'user' ? 'row-reverse' : 'row'
                    }}>
                      <div style={{
                        maxWidth: '80%',
                        padding: '14px 18px',
                        borderRadius: '20px',
                        background: chat.type === 'user' ? 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)' : 
                                   chat.type === 'system' ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' : 
                                   'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                        color: chat.type === 'user' || chat.type === 'system' ? 'white' : '#1f2937',
                        fontSize: '14px',
                        lineHeight: '1.5',
                        boxShadow: chat.type === 'user' || chat.type === 'system' ? 
                                  '0 4px 12px rgba(0, 0, 0, 0.15)' : 
                                  '0 2px 8px rgba(0, 0, 0, 0.08)',
                        border: chat.type === 'user' || chat.type === 'system' ? 'none' : '1px solid #e5e7eb'
                      }}>
                        {chat.message}
                        <div style={{
                          fontSize: '11px',
                          opacity: 0.7,
                          marginTop: '4px'
                        }}>
                          {chat.timestamp.toLocaleTimeString('ja-JP')}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

            </div>
          </div>

          {/* 管理者制御パネル */}
          <div 
            className="admin-control-panel"
            style={{
              width: '450px',
              background: 'rgba(255, 255, 255, 0.98)',
              padding: '24px',
              borderLeft: '1px solid #e2e8f0',
              backdropFilter: 'blur(20px)',
              boxShadow: '-4px 0 16px rgba(0, 0, 0, 0.05)',
              overflowY: 'auto',
              maxHeight: 'calc(100vh - 120px)',
              scrollbarWidth: 'thin',
              scrollbarColor: '#cbd5e1 #f1f5f9'
            }}>
            <h3 style={{ 
              color: '#1e40af',
              marginBottom: '24px',
              fontSize: '22px',
              fontWeight: '700',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              borderBottom: '2px solid #e2e8f0',
              paddingBottom: '12px'
            }}>
              ⚙️ 管理者制御
            </h3>


            {/* システムログ */}
            <div style={{ marginBottom: '28px' }}>
              <h4 style={{ 
                fontSize: '16px', 
                marginBottom: '12px', 
                color: '#1e40af',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                📊 システムログ
              </h4>
              <div style={{
                background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                border: '1px solid #e2e8f0',
                borderRadius: '12px',
                padding: '16px',
                height: '250px',
                overflowY: 'auto',
                fontSize: '12px',
                boxShadow: 'inset 0 2px 8px rgba(0, 0, 0, 0.05)'
              }}>
                {systemLogs.map((log, index) => (
                  <div key={index} style={{
                    marginBottom: '8px',
                    padding: '8px 12px',
                    borderRadius: '8px',
                    background: log.type === 'error' ? '#fef2f2' : 
                               log.type === 'warning' ? '#fffbeb' : 
                               log.type === 'success' ? '#f0fdf4' : '#f8fafc',
                    border: `1px solid ${log.type === 'error' ? '#fecaca' : 
                                         log.type === 'warning' ? '#fed7aa' : 
                                         log.type === 'success' ? '#bbf7d0' : '#e2e8f0'}`,
                    fontSize: '11px',
                    lineHeight: '1.4'
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}>
                      <span style={{
                        color: log.type === 'error' ? '#dc2626' : 
                               log.type === 'warning' ? '#d97706' : 
                               log.type === 'success' ? '#059669' : '#6b7280',
                        fontWeight: '600'
                      }}>
                        {log.type === 'error' ? '❌' : 
                         log.type === 'warning' ? '⚠️' : 
                         log.type === 'success' ? '✅' : 'ℹ️'}
                      </span>
                      <span style={{
                        color: '#374151',
                        fontSize: '10px',
                        fontFamily: 'monospace'
                      }}>
                        {log.timestamp.toLocaleTimeString()}
                      </span>
                    </div>
                    <div style={{
                      marginTop: '4px',
                      color: '#1f2937',
                      fontWeight: '500'
                    }}>
                      {log.message}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* 来客統計 */}
            <div>
              <h4 style={{ 
                fontSize: '16px', 
                marginBottom: '12px', 
                color: '#1e40af',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                👥 本日の来客
              </h4>
              <div style={{
                background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                border: '1px solid #e2e8f0',
                borderRadius: '12px',
                padding: '16px',
                height: '320px',
                overflowY: 'auto',
                fontSize: '12px',
                boxShadow: 'inset 0 2px 8px rgba(0, 0, 0, 0.05)'
              }}>
                {visitorStats.length === 0 ? (
                  <div style={{ color: '#999' }}>来客データがありません</div>
                ) : (
                  visitorStats.slice(-10).map((visitor, index) => (
                    <div key={index} style={{ 
                      marginBottom: '12px',
                      padding: '12px 14px',
                      backgroundColor: '#ffffff',
                      borderRadius: '10px',
                      fontSize: '11px',
                      border: '1px solid #e5e7eb',
                      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
                    }}>
                      <div style={{ 
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        marginBottom: '6px'
                      }}>
                        <span style={{
                          background: '#1e40af',
                          color: 'white',
                          padding: '2px 6px',
                          borderRadius: '4px',
                          fontSize: '9px',
                          fontWeight: '600',
                          fontFamily: 'monospace'
                        }}>
                          {visitor.timestamp.toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit' })}
                        </span>
                        <span style={{ 
                          fontWeight: 'bold', 
                          color: '#1f2937',
                          fontSize: '12px'
                        }}>
                          {visitor.visitorName}様
                        </span>
                      </div>
                      <div style={{ 
                        color: '#6b7280', 
                        fontSize: '10px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}>
                        <span style={{
                          background: '#f3f4f6',
                          padding: '2px 6px',
                          borderRadius: '4px',
                          fontWeight: '500'
                        }}>
                          {visitor.company}
                        </span>
                        <span>•</span>
                        <span>{visitor.purpose}</span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>

      </div>
    </ErrorBoundary>
  )
}

export default AdminReceptionLayout